// Area matching utilities for Hong Kong locations
import { levenshteinDistance } from './stringUtils';

interface LocationData {
  _id: string;
  english: string;
  chinese: string;
  location_code?: string;
  locationCode?: string;
  [key: string]: unknown;
}

export interface RegionData {
  _id: string;
  region_code?: string;
  region_chinese: string;
  region_english: string;
  locations?: LocationData[];
}

interface EnrichedLocation extends LocationData {
  region_id: string;
  region_code?: string;
  region_chinese: string;
  region_english: string;
}

interface MatchEntry {
  location: EnrichedLocation;
  score: number;
  matchReasons: string[];
}

export interface MatchedArea {
  location_id: string;
  location_code?: string;
  chinese: string;
  english: string;
  region_id: string;
  region_code?: string;
  region_chinese: string;
  region_english: string;
  [key: string]: unknown;
}

export interface PlaceData {
  address: string;
  coordinates?: number[] | [number, number] | null;
  place_id?: string;
  placeId?: string;
  name?: string;
}

export interface MatchResult {
  match: MatchedArea | null;
  confidence: number;
  suggestions: Array<EnrichedLocation & { score: number; reasons: string[] }>;
  debugInfo: {
    originalAddress: string;
    totalLocationsChecked: number;
    topScores: Array<{ name: string; score: number }>;
  };
  [key: string]: unknown;
}

interface CoordinateMatchResult {
  match: null;
  confidence: number;
}

interface FormattedAreaMatch {
  displayText: string;
  confidence: string;
  fullMatch: MatchedArea;
}

interface AutoSelectResult {
  shouldAutoSelect: boolean;
  match: MatchedArea | null;
  confidence: number;
  reason?: string;
}

interface MatchOptions {
  autoSelectThreshold?: number;
  showSuggestionsThreshold?: number;
  enableAutoSelect?: boolean;
}

export interface MatchWithAutoSelectResult extends MatchResult {
  shouldAutoSelect: boolean;
  shouldShowSuggestion: boolean;
  autoSelectReason?: string;
}

/**
 * Matches a Google Places result to Hong Kong area data
 * @param placeData - Google Places data with address, coordinates, place_id
 * @param areaData - Hong Kong area data from database
 * @returns Best matching area with confidence score
 */
export const matchPlaceToArea = (placeData: PlaceData, areaData: RegionData[]): MatchResult => {
  if (!placeData || !placeData.address || !areaData || !Array.isArray(areaData)) {
    return { match: null, confidence: 0, suggestions: [], debugInfo: { originalAddress: '', totalLocationsChecked: 0, topScores: [] } };
  }

  const { address } = placeData;
  const addressLower = address.toLowerCase();

  // Extract all locations from area data
  const allLocations: EnrichedLocation[] = [];
  areaData.forEach(region => {
    if (region.locations && Array.isArray(region.locations)) {
      region.locations.forEach(location => {
        allLocations.push({
          ...location,
          region_id: region._id,
          region_code: region.region_code,
          region_chinese: region.region_chinese,
          region_english: region.region_english
        });
      });
    }
  });

  // Scoring system for matching
  const matches: MatchEntry[] = allLocations.map(location => {
    let score = 0;
    const maxScore = 100;

    // 1. Exact name matching (highest priority)
    if (addressLower.includes(location.english.toLowerCase())) {
      score += 40;
    }
    if (addressLower.includes(location.chinese)) {
      score += 40;
    }

    // 2. Fuzzy string matching for partial matches
    const englishSimilarity = calculateStringSimilarity(addressLower, location.english.toLowerCase());
    const chineseSimilarity = calculateStringSimilarity(addressLower, location.chinese);
    score += Math.max(englishSimilarity, chineseSimilarity) * 30;

    // 3. Location code matching (for common abbreviations)
    const locationCode = location.location_code || location.locationCode || '';
    if (locationCode && addressLower.includes(locationCode.replace(/_/g, ' '))) {
      score += 20;
    }

    // 4. Regional context matching
    const regionScore = calculateRegionalScore(addressLower, location);
    score += regionScore * 10;

    return {
      location,
      score: Math.min(score, maxScore),
      matchReasons: getMatchReasons(addressLower, location, score)
    };
  });

  // Sort by score descending
  matches.sort((a, b) => b.score - a.score);

  // Get top matches with reasonable confidence
  const topMatches = matches.filter(match => match.score > 20).slice(0, 3);

  const bestMatch = topMatches.length > 0 ? topMatches[0] : null;
  const confidence = bestMatch ? Math.round(bestMatch.score) : 0;

  return {
    match: bestMatch ? {
      location_id: bestMatch.location._id,
      location_code: bestMatch.location.location_code || bestMatch.location.locationCode,
      chinese: bestMatch.location.chinese,
      english: bestMatch.location.english,
      region_id: bestMatch.location.region_id,
      region_code: bestMatch.location.region_code,
      region_chinese: bestMatch.location.region_chinese,
      region_english: bestMatch.location.region_english
    } : null,
    confidence,
    suggestions: topMatches.map(match => ({
      ...match.location,
      score: Math.round(match.score),
      reasons: match.matchReasons
    })),
    debugInfo: {
      originalAddress: address,
      totalLocationsChecked: allLocations.length,
      topScores: matches.slice(0, 5).map(m => ({
        name: m.location.english,
        score: Math.round(m.score)
      }))
    }
  };
};

/**
 * Calculate string similarity using multiple algorithms
 */
const calculateStringSimilarity = (str1: string, str2: string): number => {
  if (!str1 || !str2) return 0;

  // Levenshtein distance based similarity
  const maxLen = Math.max(str1.length, str2.length);
  if (maxLen === 0) return 1;

  const distance = levenshteinDistance(str1, str2);
  const levenshteinSimilarity = (maxLen - distance) / maxLen;

  // Substring matching
  const substringScore = str1.includes(str2) || str2.includes(str1) ? 0.8 : 0;

  // Word overlap
  const words1 = str1.split(/\s+/);
  const words2 = str2.split(/\s+/);
  const commonWords = words1.filter(word => words2.includes(word)).length;
  const wordOverlapScore = commonWords / Math.max(words1.length, words2.length);

  return Math.max(levenshteinSimilarity, substringScore, wordOverlapScore);
};

/**
 * Calculate regional context score
 */
const calculateRegionalScore = (address: string, location: EnrichedLocation): number => {
  const regionalKeywords: Record<string, string[]> = {
    hong_kong_island: ['central', 'admiralty', 'causeway bay', 'wan chai', 'north point', 'quarry bay', 'taikoo', 'shau kei wan', 'chai wan', 'aberdeen', 'repulse bay', 'stanley', 'mid-levels', 'sheung wan', 'sai wan'],
    kowloon: ['tsim sha tsui', 'jordan', 'yau ma tei', 'mong kok', 'prince edward', 'sham shui po', 'kowloon tong', 'hung hom', 'kwun tong', 'diamond hill', 'wong tai sin'],
    new_territories: ['sha tin', 'tai po', 'fanling', 'sheung shui', 'yuen long', 'tuen mun', 'tsuen wan', 'kwai chung', 'sai kung', 'tseung kwan o', 'ma on shan'],
    outlying_islands: ['cheung chau', 'lamma island', 'peng chau', 'lantau', 'tung chung', 'mui wo', 'tai o', 'discovery bay']
  };

  const regionKeywords = (location.region_code ? regionalKeywords[location.region_code] : []) || [];
  const keywordMatches = regionKeywords.filter(keyword =>
    address.includes(keyword.toLowerCase())
  ).length;

  return keywordMatches > 0 ? 1 : 0;
};

/**
 * Get human-readable match reasons
 */
const getMatchReasons = (address: string, location: EnrichedLocation, score: number): string[] => {
  const reasons: string[] = [];

  if (address.includes(location.english.toLowerCase())) {
    reasons.push(`Exact match: "${location.english}"`);
  }
  if (address.includes(location.chinese)) {
    reasons.push(`Exact match: "${location.chinese}"`);
  }
  if (score > 50) {
    reasons.push('High similarity score');
  } else if (score > 30) {
    reasons.push('Moderate similarity score');
  }

  return reasons;
};

/**
 * Auto-suggest area based on coordinates (if available)
 * This would require coordinate data in your area database
 */
export const matchCoordinatesToArea = (): CoordinateMatchResult => {
  // This would need coordinate data in your area database
  // For now, return null as coordinates are empty in your data
  // Future implementation would use coordinates and areaData parameters
  return { match: null, confidence: 0 };
};

/**
 * Format area match result for display
 */
export const formatAreaMatch = (matchResult: MatchResult, language: string = 'en'): FormattedAreaMatch | null => {
  if (!matchResult.match) return null;

  const { match, confidence } = matchResult;
  const displayName = language === 'zh' ? match.chinese : match.english;
  const regionName = language === 'zh' ? match.region_chinese : match.region_english;

  return {
    displayText: `${displayName} (${regionName})`,
    confidence: `${confidence}% match`,
    fullMatch: match
  };
};

/**
 * Automatically selects the best area match if confidence is high enough
 * @param matchResult - Result from matchPlaceToArea
 * @param autoSelectThreshold - Minimum confidence to auto-select (default: 70)
 * @returns Auto-selection result with shouldAutoSelect flag
 */
export const shouldAutoSelectArea = (matchResult: MatchResult, autoSelectThreshold: number = 70): AutoSelectResult => {
  if (!matchResult || !matchResult.match || matchResult.confidence < autoSelectThreshold) {
    return {
      shouldAutoSelect: false,
      match: null,
      confidence: 0
    };
  }

  return {
    shouldAutoSelect: true,
    match: matchResult.match,
    confidence: matchResult.confidence,
    reason: `Auto-selected with ${matchResult.confidence}% confidence`
  };
};

/**
 * Matches a Google Places result to Hong Kong area data with auto-selection support
 * @param placeData - Google Places data with address, coordinates, place_id
 * @param areaData - Hong Kong area data from database
 * @param options - Options for matching behavior
 * @returns Best matching area with confidence score and auto-select info
 */
export const matchPlaceToAreaWithAutoSelect = (placeData: PlaceData, areaData: RegionData[], options: MatchOptions = {}): MatchWithAutoSelectResult => {
  const {
    autoSelectThreshold = 70,
    showSuggestionsThreshold = 30,
    enableAutoSelect = true
  } = options;

  // Get the basic match result
  const matchResult = matchPlaceToArea(placeData, areaData);

  if (!matchResult.match) {
    return {
      ...matchResult,
      shouldAutoSelect: false,
      shouldShowSuggestion: false
    };
  }

  // Check if we should auto-select
  const autoSelectResult = shouldAutoSelectArea(matchResult, autoSelectThreshold);

  return {
    ...matchResult,
    shouldAutoSelect: enableAutoSelect && autoSelectResult.shouldAutoSelect,
    shouldShowSuggestion: !autoSelectResult.shouldAutoSelect && matchResult.confidence >= showSuggestionsThreshold,
    autoSelectReason: autoSelectResult.reason
  };
};
